/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    AddressAddUpdate,
    AddressCommon,
    AddressFormat,
    AddressItemDisplayType,
    AddressItemType,
    AddressMetaData,
    AddressOperation,
    AddressType,
    AddressValidationRuleType,
    AutoSuggest,
    IAddressAddUpdateProps,
    IAddressItem,
    IAddressResource,
    IAddressResponse,
    ISuggestion
} from '@msdyn365-commerce-modules/address';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { Address, AddressPurpose, CountryRegionInfo, StateProvinceInfo } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { action, observable, reaction, set } from 'mobx';
import * as React from 'react';
import { ICipBusinessAccountAddressData } from './cip-business-account-address.data';
import { ICipBusinessAccountAddressProps } from './cip-business-account-address.props.autogenerated';
import SignUpError from './components/sign-up-error';

export type AddressValidationErrors = Record<string, string | null>;

/**
 * Interface for IBusinessAccountAddressExtendedProps.
 */
export interface IBusinessAccountAddressExtendedProps extends ICipBusinessAccountAddressProps<ICipBusinessAccountAddressData> {
    hasError: boolean;
    forceValidation?: boolean;
    isRequired: boolean;
    resetAddress: boolean;
    validationError?: AddressValidationErrors;
    updateForm(errors: boolean, currentAddress?: Address, validationErrors?: AddressValidationErrors): void;
}

/**
 * Interface for IBusinessAccountAddressState.
 */
export interface IBusinessAccountAddressState {
    countryId?: string;
    states?: StateProvinceInfo[];
}

/**
 * Interface for IBusinessAccountAddressViewProps.
 */
export interface IBusinessAccountAddressViewProps {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    addressListSelectedAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError?: AddressValidationErrors;
    addressActionResponse?: IAddressResponse;
    BusinessAccountAddress: IModuleProps;
    isUpdating: boolean;
    hasError: boolean;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
}

/**
 *
 * Address component.
 * @extends {React.Component<ICipBusinessAccountAddressProps<ICipBusinessAccountAddressData>, IBusinessAccountAddressState>}
 */
class BusinessAccountAddress extends React.Component<IBusinessAccountAddressExtendedProps, IBusinessAccountAddressState> {
    @observable private addUpdateAddress: Address;

    @observable private countryRegionId: string = 'USA';

    @observable private stateProvinceInfo?: StateProvinceInfo[];

    @observable private validationError: AddressValidationErrors;

    private readonly addressActionResponse?: IAddressResponse;

    private readonly isUpdating?: boolean;

    private readonly addressCommon: AddressCommon;

    private addressFormat: AddressFormat;

    private countryRegions: CountryRegionInfo[] = [];

    private addressPurposes: AddressPurpose[] = [];

    private readonly resources: IAddressResource;

    private readonly defaultAddressType: number = 0; // Default to None (This value set in HQ as Business)

    private readonly shouldAutoFocus?: boolean = false;

    private readonly excludedList: AddressItemType[] = [
        AddressItemType.Name,
        AddressItemType.AddressTypeValue,
        AddressItemType.IsPrimary,
        AddressItemType.Postbox
    ]; // Default to hiding Name, AddressType and Postbox inputs

    private readonly telemetryContent: ITelemetryContent;
    private autoSuggest?: AutoSuggest;
    private useAzureMaps: boolean;

    public constructor(props: IBusinessAccountAddressExtendedProps) {
        super(props);

        const { context, data, resources, telemetry } = this.props;
        this.addUpdateAddress = {};
        this.resources = resources;
        this.countryRegions = data.countryRegions.result || [];
        this.addressPurposes = data.addressPurposes.result || [];
        this.stateProvinceInfo = data.countryStates.result || [];
        this.addressFormat = this._buildAddressFormat(props.isRequired, false);
        this.addressCommon = new AddressCommon(context, resources, telemetry);
        this.validationError = {};
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.useAzureMaps = false;
    }

    public componentDidMount(): void {
        this._dataInitialize(this.props);

        const {
            context: {
                actionContext: {
                    requestContext: { channel }
                }
            },
            config: { autoSuggestionEnabled, autoSuggestOptions }
        } = this.props as any;

        this._setDefaultCountryRegionId();

        this.useAzureMaps = channel?.AzureMapsEnabled ?? false;

        if (!this.useAzureMaps && autoSuggestionEnabled) {
            // Only initialize AutoSuggest when not using Azure Maps and config allows it
            if (channel && !channel.BingMapsApiKey) {
                this.props.telemetry && this.props.telemetry.error && this.props.telemetry.error('BingMapsApiKey is missing.');
            } else if (channel && !channel.BingMapsEnabled) {
                this.props.telemetry && this.props.telemetry.error && this.props.telemetry.error('Map is disabled from HQ.');
            } else {
                try {
                    const options = { ...(autoSuggestOptions || {}) };
                    this.autoSuggest = new AutoSuggest(
                        (this.props as any).telemetry,
                        options,
                        channel?.BingMapsApiKey,
                        channel?.ChannelCountryRegionISOCode,
                        channel?.DefaultLanguageId
                    );
                } catch (e) {
                    // swallow - AutoSuggest is optional
                }
            }
        }

        this._evaluateAddressAndNotify();
    }

    public async componentDidUpdate(prevProps: IBusinessAccountAddressExtendedProps): Promise<void> {
        if ((this.props.hasError && !prevProps.hasError) || this.props.forceValidation !== prevProps.forceValidation) {
            this._evaluateAddressAndNotify();
        }

        // If storeSelectorStateManager exists and map API is loaded, attempt to attach autosuggest
        if (this.props.data && (this.props.data as any).storeSelectorStateManager?.result?.isMapApiLoaded) {
            await this._attachMapAutoSuggest();
        }
    }

    public shouldComponentUpdate(nextProps: IBusinessAccountAddressExtendedProps, nextState: IBusinessAccountAddressState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }
    /**
     * On suggestion selected when using Azure maps.
     * @param result - Suggestion result interface.
     */
    @action
    private readonly onAzureMapsSuggestionSelected = async (result: ISuggestion): Promise<void> => {
        // Respect config flag to disable auto suggestions entirely
        const { config } = this.props;
        if (!config?.autoSuggestionEnabled) {
            return;
        }

        this.handleAzureMapsSuggestionSelected1(result, this.stateProvinceInfo, (fieldName: string, value: string) => {
            set(this.addUpdateAddress, { [fieldName]: value });
        });

        // After populating fields, run validation and notify parent form
        this._evaluateAddressAndNotify();
    };

    public handleAzureMapsSuggestionSelected1(
        suggestion: ISuggestion,
        _stateProvinceInfo?: StateProvinceInfo[],
        addressSetter?: (fieldName: string, value: string) => void
    ): Address {
        const address = this.getTranformedAzureMapsAddress(suggestion);
        if (!addressSetter) {
            return address;
        }

        // Reset all relevant fields (ensures no stale values)
        this._resetAutoCompleteAddressFields(addressSetter);

        // Populate with current suggestion
        const mapValue = (key: string, val?: string) => addressSetter(key, val || '');
        mapValue('ZipCode', address.ZipCode);
        mapValue('CountyName', address.CountyName);
        mapValue('City', address.City);
        mapValue('State', address.State || address.StateName);
        mapValue('DistrictName', address.DistrictName);
        mapValue('FullAddress', address.FullAddress);
        // Set Street last in case UI applies formatting/focus logic
        mapValue('Street', address.Street);
        mapValue('StreetNumber', (address as any).StreetNumber);

        return address;
    }

    public getTranformedAzureMapsAddress = (result: ISuggestion): Address => {
        const address: Address = {};

        // Zip Code
        address.ZipCode = result.address.postalCode || '';

        // Preferred: take explicit Azure Maps fields.
        let streetNameRaw: string = result.address.streetName || '';
        let streetNumberRaw: string = result.address.streetNumber || '';

        // Fallback: attempt to parse trailing number when Azure Maps omits streetNumber but embeds it.
        if (!streetNumberRaw && streetNameRaw) {
            // Examples handled: "Oxford Street 12", "Rue de la Paix 5B", "Mainstrasse 10/2" etc.
            const match = streetNameRaw.match(/^(.*?)[,\s]+(\d+[A-Za-z0-9\/\-]*)$/);
            if (match) {
                streetNameRaw = match[1];
                streetNumberRaw = match[2];
            }
        }

        // Assign split fields. Keep Street as pure name; StreetNumber as number/extension.
        if (streetNameRaw) {
            (address as any).Street = streetNameRaw.trim();
        } else {
            (address as any).Street = '';
        }
        if (streetNumberRaw) {
            (address as any).StreetNumber = streetNumberRaw.trim();
        }

        address.City = result.address.municipality || '';
        address.DistrictName = result.address.countrySecondarySubdivision || '';
        address.CountyName = result.address.countrySecondarySubdivision || '';

        address.State = result.address.countrySubdivisionCode || '';
        address.StateName = result.address.countrySubdivisionName || '';

        address.FullAddress = result.address.freeformAddress || '';

        return address;
    };

    /**
     * Clears the autocomplete-controlled address fields so missing values from a new suggestion do not retain old data.
     */
    private _resetAutoCompleteAddressFields(addressSetter: (field: string, value: string) => void): void {
        const fields = ['Street', 'StreetNumber', 'ZipCode', 'CountyName', 'City', 'State', 'StateName', 'DistrictName', 'FullAddress'];
        fields.forEach(f => addressSetter(f, ''));
    }

    private _captureValidationSnapshot(): AddressValidationErrors | undefined {
        const entries = Object.entries(this.validationError || {}).filter(([, message]) => {
            return typeof message === 'string' && message.length > 0;
        });
        if (entries.length === 0) {
            return undefined;
        }

        return entries.reduce<AddressValidationErrors>((acc, [field, message]) => {
            if (typeof message === 'string') {
                Object.assign(acc, { [field]: message });
            }

            return acc;
        }, {});
    }

    private _clearValidationErrors(): void {
        this.validationError = {};
    }

    public render(): JSX.Element | null {
        const { renderView, config, hasError, resetAddress } = this.props;

        // Business-sign-up will pass this flag when cancel button is clicked
        if (resetAddress) {
            this._resetAddressFields();
        }

        const validationErrors = this.props.validationError ?? this.validationError;

        const viewProps = {
            ...this.props,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            validationError: validationErrors,
            addressActionResponse: this.addressActionResponse,
            hasError,
            className: config.className,
            BusinessAccountAddress: {
                moduleProps: this.props,
                className: classnames('ms-business-account-address', config.className)
            },
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress()
        };

        return renderView(viewProps) as React.ReactElement;
    }

    /**
     * Reset Address.
     */
    public _resetAddressFields = () => {
        const addressFormat = this.addressFormat.getAddressFormat(this.countryRegionId);

        addressFormat.map(addressFormatItem => {
            switch (addressFormatItem.displayType) {
                case AddressItemDisplayType.Input:
                    (this.addUpdateAddress as any)[addressFormatItem.name] = '';
                    break;
                case AddressItemDisplayType.Checkbox:
                    (this.addUpdateAddress as any)[addressFormatItem.name] = false;
                    break;
                default:
                    switch (addressFormatItem.type) {
                        case AddressItemType.ThreeLetterISORegionName:
                            (this.addUpdateAddress as any)[addressFormatItem.name] = this.countryRegionId;
                            break;
                        case AddressItemType.AddressTypeValue:
                            (this.addUpdateAddress as any)[addressFormatItem.name] = this.defaultAddressType;
                            break;
                        case AddressItemType.State:
                            (this.addUpdateAddress as any)[addressFormatItem.name] = '';
                            break;
                        default:
                            (this.addUpdateAddress as any)[addressFormatItem.name] = undefined;
                    }
            }
        });

        this._clearValidationErrors();
        this._evaluateAddressAndNotify();
    };

    /**
     * Method data initialization.
     * @param props -The account management props.
     */
    private readonly _dataInitialize = (props: IBusinessAccountAddressExtendedProps): void => {
        const { data } = props;

        reaction(
            () => data.countryRegions.result,
            () => {
                this.countryRegions = data.countryRegions.result ?? [];
            }
        );

        reaction(
            () => data.addressPurposes.result,
            () => {
                this.addressPurposes = data.addressPurposes.result ?? [];
            }
        );

        reaction(
            () => data.countryStates.result,
            () => {
                this.stateProvinceInfo = data.countryStates.result ?? [];
            }
        );
    };

    // Set validationError and hasError to hide error messages since in the B2B
    // request form, no errors would visually show since user wont be able to submit
    // until form is filled and correct
    /**
     * Render Add Or Update Address Function.
     * @returns - AddressAddUpdate.
     */
    private readonly _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
        // Always use observable validationError to avoid MobX 'has()' warnings on plain objects.
        const addUpdateProps = AddressAddUpdate({
            isUpdating: this.isUpdating,
            resources: this.resources,
            addressType: AddressType.Company,
            addressFormat: this.addressFormat.getAddressFormat(this.countryRegionId),
            defaultCountryRegionId: this.countryRegionId,
            defaultAddressType: this.defaultAddressType,
            selectedAddress: this.addUpdateAddress,
            shouldAutoFocus: this.shouldAutoFocus,
            validationError: this.validationError,
            hasError: this.props.hasError,
            addressActionResponse: this.addressActionResponse,
            telemetryContent: this.telemetryContent,
            dropdownDisplayData: this.addressFormat.getPrefilledAddressDropdownData(
                this.resources.addressStateDefaultSelectionText,
                this.stateProvinceInfo
            ),
            onInputChange: this._onAddressAddUpdateInputChange,
            onDropdownChange: this._onAddressAddUpdateDropdownChange,
            channel: this.props.context.request.channel,
            locale: this.props.context.request.locale,
            onAzureMapsSuggestionSelected:
                this.props.config && this.props.config.autoSuggestionEnabled === true
                    ? this.onAzureMapsSuggestionSelected.bind(this)
                    : undefined
        });

        // If auto suggestions are disabled, replace the 'Street' AddressInput component with a plain input
        // so the Azure inline suggestion network calls (inside the AddressInput component) are not triggered.
        const autoSuggestionEnabled = this.props.config && this.props.config.autoSuggestionEnabled === true;
        if (!autoSuggestionEnabled && addUpdateProps && Array.isArray(addUpdateProps.items)) {
            addUpdateProps.items = addUpdateProps.items.map(item => {
                try {
                    if (item && item.key && item.key.toLowerCase() === 'street') {
                        // Derive input id from container id if available (address-add uses `${elementId}_container`)
                        const containerId = item.AddressItem && (item.AddressItem as any).id;
                        const inputId =
                            containerId && typeof containerId === 'string' && containerId.endsWith('_container')
                                ? containerId.replace(/_container$/, '')
                                : `${(AddressType as any).toString ? (AddressType as any).toString() : 'address'}_addressstreet`;

                        const inputElement = (
                            <input
                                name={item.key}
                                id={inputId}
                                className={'msc-address-form__input'}
                                type={'text'}
                                autoFocus={false}
                                value={(this.addUpdateAddress as any)[item.key] || ''}
                                onChange={this._onAddressAddUpdateInputChange}
                            />
                        );

                        return {
                            ...item,
                            input: inputElement
                        };
                    }
                } catch (e) {
                    // no-op fallback to original item
                }

                return item;
            });
        }

        if (addUpdateProps && Array.isArray(addUpdateProps.items)) {
            addUpdateProps.items = addUpdateProps.items.map(item => {
                if (!item) {
                    return item;
                }
                // Determine required by inspecting validation rules (robust version without ternary formatting issues)
                let isRequired = false;
                const validationRulesForField = (item as any).validationRules;
                if (validationRulesForField && Array.isArray(validationRulesForField)) {
                    isRequired = validationRulesForField.some(
                        (validationRuleObject: any) =>
                            validationRuleObject && validationRuleObject.type === AddressValidationRuleType.Required
                    );
                }

                // Use centralized helpers for required and alert message logic
                const itemKeyLowerCase = this._getItemKeyLowerCase(item);
                if (itemKeyLowerCase === 'streetnumber') {
                    isRequired = true;
                } else {
                    isRequired = isRequired || this._itemHasRequiredValidationRule(item);
                }

                const rawValidationError = (this.validationError as any)[item.key];
                const hasFieldError = rawValidationError !== null && rawValidationError !== undefined && rawValidationError !== '';
                let alertNode = item.alert;
                // Show required error if field is required and empty/undefined, not just empty string
                const fieldValue = (this.addUpdateAddress as any)[item.key];
                const isFieldEmpty = fieldValue === '' || fieldValue === undefined || fieldValue === null;
                if (this.props.hasError && (hasFieldError || (isRequired && isFieldEmpty))) {
                    const alertMessage = this._resolveAlertMessageForField(itemKeyLowerCase, rawValidationError);
                    alertNode = <SignUpError id={`${item.key}-error`} variant='danger' message={alertMessage} />;
                }
                return {
                    ...item,
                    isRequired,
                    alert: alertNode
                } as any;
            });
        }

        return addUpdateProps;
    };

    /**
     * Resolves a field level error message.
     */
    private readonly _resolveFieldErrorMessage = (raw: any): string => {
        return typeof raw === 'string' ? raw : '';
    };

    // Helper: safe lower-cased key for an item
    private readonly _getItemKeyLowerCase = (item: any): string => {
        return String(item?.key ?? '').toLowerCase();
    };

    // Helper: ensure a Required validation rule exists on an address format item
    private readonly _addRequiredValidationRuleIfMissing = (addressFormatItem: any, messageResource?: string): void => {
        if (!Array.isArray(addressFormatItem.validationRules)) {
            addressFormatItem.validationRules = [];
        }
        const alreadyHasRequired = addressFormatItem.validationRules.some(
            (rule: any) => rule && rule.type === AddressValidationRuleType.Required
        );
        if (!alreadyHasRequired) {
            addressFormatItem.validationRules.push({ type: AddressValidationRuleType.Required, regEx: '', message: messageResource });
        }
    };

    // Helper: determine whether an item is required by its validation rules
    private readonly _itemHasRequiredValidationRule = (item: any): boolean => {
        const rules = item?.validationRules;
        return !!(rules && Array.isArray(rules) && rules.some((r: any) => r && r.type === AddressValidationRuleType.Required));
    };

    // Helper: compute alert message for an item key and raw validation error
    private readonly _resolveAlertMessageForField = (itemKeyLowerCase: string, rawValidationError: any): string => {
        if (rawValidationError) {
            return this._resolveFieldErrorMessage(rawValidationError);
        }

        switch (itemKeyLowerCase) {
            case 'street':
                return this.resources.addressStreetRequiredErrorMessage;
            case 'streetnumber':
                return this.resources.addressStreetNumberRequiredErrorMessage;
            default:
                return this.resources.addressGenericErrorMessage;
        }
    };

    private readonly _attachMapAutoSuggest = async (): Promise<void> => {
        try {
            if (this.autoSuggest && this.props.data && (this.props.data as any).storeSelectorStateManager?.result?.isMapApiLoaded) {
                // attach to the street input id used in this module (if present in DOM)
                this.autoSuggest.attachAutoSuggest(
                    '#shipping_addressstreet',
                    '#shipping_addressstreet_container',
                    this.onAzureMapsSuggestionSelected.bind(this)
                );
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to attach autosuggest:', e);
        }
    };

    /**
     * On Address Add Update Input Change Function.
     * @param event - Input event change function.
     */
    private readonly _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this._updateAddress(event.target.name, event.target.value);
    };

    /**
     * On Address Add Update Dropdown Change Function.
     * @param event - Change dropdown event.
     */
    private readonly _onAddressAddUpdateDropdownChange = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            await this._updateCountryRegionId(event.target.value);
        } else {
            this._updateAddress(event.target.name, event.target.value);
        }
    };

    private _evaluateAddressAndNotify(): void {
        const { updateForm, isRequired } = this.props;

        const isAddressEmpty = this._isEmpty();

        // Decide when to apply required validation:
        //  - Field explicitly required (isRequired)
        //  - Parent requested validation (hasError)
        //  - User has started typing some fields (partially filled)
        const hasParentErrorFlag = this.props.hasError;
        const isPartiallyFilled = !isAddressEmpty;

        const applyRequiredRules = isRequired || hasParentErrorFlag || isPartiallyFilled;
        this.addressFormat = this._buildAddressFormat(applyRequiredRules, isAddressEmpty);

        this.validationError = {};

        let isValid = this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId);

        const streetNumberVal = this.addUpdateAddress.StreetNumber;
        const streetNumberEmpty = streetNumberVal === undefined || streetNumberVal === null || String(streetNumberVal).trim() === '';

        if (applyRequiredRules && isPartiallyFilled && streetNumberEmpty) {
            (this.validationError as any).StreetNumber = this.resources.addressStreetNumberRequiredErrorMessage;
            isValid = false;
        }

        if (isValid) {
            this._clearValidationErrors();
            updateForm(false, this.addUpdateAddress, undefined);
        } else {
            // Reassign to trigger MobX reaction on newly added keys (validateAddressFormat mutates object in place)
            this.validationError = { ...this.validationError };
            updateForm(true, this.addUpdateAddress, this._captureValidationSnapshot());
        }
    }

    /**
     * Update Address Function.
     * @param name - String.
     * @param value - String.
     */
    private readonly _updateAddress = (name: string, value: string | number) => {
        let cleanValue = value;
        if (typeof value === 'string') {
            cleanValue = (value || '').replace(new RegExp('[<>]', 'gi'), '');
        }
        set(this.addUpdateAddress, { [name]: cleanValue });
        this._evaluateAddressAndNotify();
    };

    private _buildAddressFormat(applyRequired: boolean, _isEmpty: boolean): AddressFormat {
        const optionalRequiredList: AddressItemType[] | undefined = applyRequired ? undefined : [];

        const metadata = new AddressMetaData({ ...this.resources }, [...this.excludedList, AddressItemType.State], optionalRequiredList);

        const addressFormat = new AddressFormat(this.countryRegions, metadata, this.addressPurposes);
        const addressFormatItems = addressFormat.getAddressFormat(this.countryRegionId);

        for (const addressFormatItem of addressFormatItems) {
            const isStateField = addressFormatItem.type === AddressItemType.State;
            const isStreetNumberField = addressFormatItem.type === AddressItemType.StreetNumber;

            if (isStateField) {
                addressFormatItem.validationRules = (addressFormatItem.validationRules || []).filter(
                    (validationRule: any) => validationRule.type !== AddressValidationRuleType.Required
                );
            }

            if (isStreetNumberField) {
                this._addRequiredValidationRuleIfMissing(addressFormatItem, this.resources.addressStreetNumberRequiredErrorMessage);
            }
        }

        return addressFormat;
    }

    /**
     * Is empty Function.
     * @returns - Boolean.
     */
    // Walk through array and check if valued is defined or value is not an empty string
    private readonly _isEmpty = () => {
        const addressFormat = this.addressFormat.getAddressFormat(this.countryRegionId);
        for (const addressFormatItem of addressFormat) {
            if (addressFormatItem.type !== AddressItemType.ThreeLetterISORegionName || this._isInputRequired(addressFormatItem)) {
                const addressItemValue = (this.addUpdateAddress as any)[addressFormatItem.name];

                // Check each input field, if value is defined and not an empty string --> address is not empty
                if (!ObjectExtensions.isNullOrUndefined(addressItemValue) && addressItemValue !== '') {
                    return false;
                }
            }
        }

        return true;
    };

    /**
     * Is Input Required Function.
     * @param addressFormatItem - IAddressItem.
     * @returns - Boolean.
     */
    private readonly _isInputRequired = (addressFormatItem: IAddressItem): boolean => {
        const rules = addressFormatItem?.validationRules;
        if (!rules || rules.length === 0) {
            return false;
        }
        return rules.some(r => r?.type === AddressValidationRuleType.Required);
    };

    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request.channel?.ChannelCountryRegionISOCode;

        void this._updateCountryRegionId(this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market));

        // Set default AddressType
        set(this.addUpdateAddress, { AddressTypeValue: this.defaultAddressType });
    }

    /**
     * Update Country Region Id Function.
     * @param value - String.
     */
    private readonly _updateCountryRegionId = async (value: string) => {
        this.countryRegionId = value;

        await this.addressCommon.getStateProvinces(this.countryRegionId).then((result: StateProvinceInfo[]) => {
            const stateInfo = result.some(state => state.StateId === this.addUpdateAddress.State);

            // Reset state if selected state not found in the list.
            if (!stateInfo) {
                this._updateAddress('State', '');
            }

            this.stateProvinceInfo = result;
        });

        this._updateAddress('ThreeLetterISORegionName', this.countryRegionId);
    };
}

export default BusinessAccountAddress;
